import { PauseIcon, ResumeIcon } from '@radix-ui/react-icons';
import { Badge, Box, Flex, Text } from '@radix-ui/themes';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { CommonTooltip } from 'components/common/tooltip';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { format } from 'date-fns-tz';
import { TABLES } from 'enums/tables';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { RowWithID } from 'interfaces/tables/main';
import { StringHelper } from 'lib_ts/classes/string.helper';
import {
  LOCAL_DATETIME_FORMAT_LONG,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { MachineLogLevel, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { LOGGER_NAMES, LoggerName } from 'lib_ts/interfaces/i-machine-daemons';
import {
  IReadLogMsg,
  IReadLogResponseMsg,
} from 'lib_ts/interfaces/machine-msg/i-read-logs';
import { useEffect, useMemo, useState } from 'react';
import { getSocket } from 'services/socket';
import { useAuthStore } from 'stores/use-auth-store';
import { v4 } from 'uuid';

const LINES_PER_FILE = 50;

// separates individual log messages from one another
const TERMINATOR = '\u00A0\n';

// for parsing out the header for each message
const MSG_INDEX = {
  TIMESTAMP: 0,
  LEVEL: 1,
  FULL_PROCESS: 2,
  MESSAGE: 3,
};

const getLabelColor = (level?: MachineLogLevel): RadixColor | undefined => {
  switch (level) {
    case MachineLogLevel.ERROR:
    case MachineLogLevel.CRITICAL: {
      return RADIX.COLOR.DANGER;
    }

    case MachineLogLevel.WARNING: {
      return RADIX.COLOR.WARNING;
    }

    case MachineLogLevel.INFO: {
      return RADIX.COLOR.INFO;
    }

    case MachineLogLevel.DEBUG:
    default: {
      return RADIX.COLOR.SECONDARY;
    }
  }
};

interface ILiveLogLine extends RowWithID {
  process: string; // should be LoggerName
  message: string;
  timestamp?: string;
  level?: MachineLogLevel;
}

export const LiveLogsTab = () => {
  const wsConnected = useAuthStore((s) => s.wsConnected);
  const current = useAuthStore((s) => s.current);

  const machine = useMachinesPortalStore((s) => s.machine);
  const machineID = machine?.machineID;

  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [logLines, setLoggedLines] = useState<ILiveLogLine[]>([]);
  const [filterLevel, setFilterLevel] = useState<MachineLogLevel[]>([]);
  const [filterProcess, setFilterProcess] = useState<LoggerName[]>([]);

  const [pause, setPause] = useState(false);
  const pauseRef = useLatest(pause);

  useEffect(() => {
    if (!wsConnected) {
      return;
    }

    if (!machineID) {
      return;
    }

    const socket = getSocket();

    if (!socket) {
      console.warn('connected w/o socket');
      return;
    }

    const readLog = () => {
      const msg: IReadLogMsg = {
        machineID: machineID,
        session: current.session,
        limit: LINES_PER_FILE,
        processes: filterProcess.length === 0 ? LOGGER_NAMES : filterProcess,
        // will be filled by server
        wsID: undefined,
      };

      socket.emit(WsMsgType.U2M_ReadLog, msg);
    };

    const handleReadLogResponse = (data: IReadLogResponseMsg) => {
      const lines: ILiveLogLine[] = data.files.flatMap((file) =>
        file.content
          .split(TERMINATOR)
          .filter((l) => !!l && l.split(' - ').length > 2)
          .map((line) => {
            const msgParts = line.split(' - ');
            const output: ILiveLogLine = {
              // generate a unique ID for each log line for React keys
              _id: v4(),
              // for display
              process: msgParts[MSG_INDEX.FULL_PROCESS],
              // for sorting later
              timestamp: msgParts[MSG_INDEX.TIMESTAMP],
              // for filtering later
              level: msgParts[MSG_INDEX.LEVEL] as MachineLogLevel,
              // in case the message contained " - " and there are more than 4 parts as a result
              message: msgParts
                .filter((_, i) => i >= MSG_INDEX.MESSAGE)
                .join(' - ')
                .trim(),
            };

            // logLines.push(output);
            return output;
          })
      );

      setLoggedLines(lines);
      setLastUpdated(new Date());
    };

    socket.on(WsMsgType.M2U_ReadLogResponse, handleReadLogResponse);
    readLog();

    const interval = setInterval(() => {
      if (pauseRef.current) {
        return;
      }

      readLog();
    }, 5_000);

    return () => {
      socket.off(WsMsgType.M2U_ReadLogResponse, handleReadLogResponse);
      clearInterval(interval);
    };
  }, [current.session, wsConnected]);

  const liveLog: ILiveLogLine[] = useMemo(() => {
    if (logLines.length === 0) {
      return [
        {
          _id: 'system',
          process: 'System',
          message: 'No data',
        },
      ];
    }

    const filteredLines = logLines.filter((f) => {
      if (filterProcess.length === 0) {
        return true;
      }
      return filterProcess.includes(f.process as LoggerName);
    });

    if (filteredLines.length === 0) {
      return [
        {
          _id: 'system',
          process: 'System',
          message: 'No filtered data (processes)',
        },
      ];
    }

    const filterByLevel = filteredLines.filter((l) => {
      if (filterLevel.length === 0) {
        return true;
      }
      return filterLevel.includes(l.level ?? MachineLogLevel.DEBUG);
    });

    if (filterByLevel.length === 0) {
      return [
        {
          _id: 'system',
          process: 'System',
          message: 'No filtered data (levels)',
        },
      ];
    }

    return filterByLevel.sort((a, b) =>
      StringHelper.safeSort(a.timestamp, b.timestamp)
    );
  }, [logLines, filterLevel, filterProcess]);

  return (
    <ErrorBoundary identifier="MachineDetailsLiveLogsTab">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.MD}
        className={RADIX.VFLEX.GROW}
      >
        <FlexTableWrapper
          gap={RADIX.FLEX.GAP.SECTION}
          header={
            <CommonFormGrid columns={2}>
              <CommonSearchInput
                id="live-logs-processes"
                placeholder="Processes"
                options={LOGGER_NAMES.map((o) => ({
                  label: o,
                  value: o,
                }))}
                values={filterProcess}
                onChange={(v) => setFilterProcess(v as LoggerName[])}
                multiple
              />
              <CommonSearchInput
                id="live-logs-levels"
                placeholder="Levels"
                options={Object.values(MachineLogLevel).map((o) => ({
                  label: o,
                  value: o,
                }))}
                values={filterLevel}
                onChange={(v) => setFilterLevel(v as MachineLogLevel[])}
                multiple
              />
            </CommonFormGrid>
          }
          table={
            <CommonTableHoC
              dataTestID="MachineLiveLogs"
              identifier="MachineLiveLogsTable"
              displayColumns={[
                {
                  label: 'Timestamp',
                  key: 'timestamp',
                  formatFn: (l: ILiveLogLine) =>
                    l.timestamp
                      ? format(
                          new Date(l.timestamp.split(',')[0]),
                          LOCAL_DATETIME_FORMAT_LONG,
                          { timeZone: LOCAL_TIMEZONE }
                        )
                      : '--',
                },
                {
                  label: 'Process',
                  key: 'process',
                  formatFn: (l: ILiveLogLine) => (
                    <Badge color={RADIX.COLOR.WARNING}>
                      {l.process.replaceAll('.', ' > ')}
                    </Badge>
                  ),
                },
                {
                  label: 'Level',
                  key: 'level',
                  formatFn: (l: ILiveLogLine) => (
                    <Badge color={getLabelColor(l.level)}>
                      {l.level ?? MachineLogLevel.CRITICAL}
                    </Badge>
                  ),
                },

                {
                  label: 'Message',
                  key: 'message',
                  formatFn: (l: ILiveLogLine) => (
                    <CommonTooltip
                      trigger={
                        <Box className="clamp-message" py="1">
                          <Text>{l.message}</Text>
                        </Box>
                      }
                      text={l.message}
                    />
                  ),
                },
              ]}
              displayData={liveLog}
              enableSort
              defaultSort={{ key: 'timestamp', dir: 1 }}
              enablePagination
              pageSizes={TABLES.PAGE_SIZES.SM}
              footerLeftSlot={
                <Box mr="auto">
                  {lastUpdated && (
                    <Text size={RADIX.TEXT.SIZE.SM}>
                      {t('common.updated')}:{' '}
                      {format(lastUpdated, LOCAL_DATETIME_FORMAT_LONG, {
                        timeZone: LOCAL_TIMEZONE,
                      })}
                    </Text>
                  )}
                </Box>
              }
              footerRightSlot={
                // compensate for default gap applied by TableFooter
                <Box ml="-4">
                  <DialogButton
                    icon={pause ? <ResumeIcon /> : <PauseIcon />}
                    label={pause ? 'Resume' : 'Pause'}
                    color={pause ? RADIX.COLOR.SUCCESS : undefined}
                    onClick={() => setPause((p) => !p)}
                  />
                </Box>
              }
              vFlex
            />
          }
        />
      </Flex>
    </ErrorBoundary>
  );
};
